/**
 * Page Activité - Administration
 * Connexions récentes et suivi d'activité
 */

import { useQuery } from '@tanstack/react-query';
import {
  Activity, LogIn, User, AlertTriangle, Clock,
  CheckCircle, XCircle, RefreshCw,
} from 'lucide-react';
import { userAdminApi } from '../../infrastructure/api/userAdminApi';
import type { UserAdminDTO, UserAdminStatsDTO } from '../../application/dto/dtos';

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function tempsDepuis(dateStr?: string): string {
  if (!dateStr) return 'Jamais';
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "À l'instant";
  if (min < 60) return `Il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `Il y a ${h}h`;
  return `Il y a ${Math.floor(h / 24)}j`;
}

export function ActivityPage() {
  const { data: statsData } = useQuery({
    queryKey: ['userAdmin', 'stats'],
    queryFn: () => userAdminApi.obtenirStatistiques(),
  });

  const { data: usersData, isLoading, refetch } = useQuery({
    queryKey: ['userAdmin', 'activite'],
    queryFn: () => userAdminApi.listerUtilisateurs({ page: 1, limit: 100 }),
  });

  const stats: UserAdminStatsDTO | undefined = statsData?.data?.statistiques;
  const users: UserAdminDTO[] = usersData?.data?.donnees?.utilisateurs ?? [];

  const sorted = [...users].sort((a, b) => {
    const da = a.lastLogin ?? a.dateDerniereConnexion ?? '';
    const db = b.lastLogin ?? b.dateDerniereConnexion ?? '';
    return db.localeCompare(da);
  });

  const jamaisConnectes = sorted.filter(u => !u.lastLogin && !u.dateDerniereConnexion);
  const ayantConnecte = sorted.filter(u => u.lastLogin || u.dateDerniereConnexion);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-content">Activité</h1>
          <p className="mt-1 text-sm text-content-muted">
            Connexions récentes et suivi d'activité des utilisateurs
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-2 text-sm text-content-muted border border-border rounded-lg hover:bg-surface-raised transition-colors"
        >
          <RefreshCw size={14} />
          Actualiser
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Connexions récentes', value: stats?.connexionsRecentes, sub: '30 derniers jours', icon: Activity, color: 'text-primary-text' },
          { label: 'Ont déjà connecté', value: ayantConnecte.length, sub: 'sur ' + users.length + ' comptes', icon: CheckCircle, color: 'text-success' },
          { label: 'Jamais connectés', value: jamaisConnectes.length, sub: 'Compte inutilisé', icon: XCircle, color: 'text-warning' },
          { label: 'Comptes bloqués', value: stats?.comptesBloques, sub: 'Verrouillés', icon: AlertTriangle, color: 'text-danger' },
        ].map((s) => (
          <div key={s.label} className="bg-surface rounded-lg border border-border p-4">
            <div className="flex items-center gap-2 mb-1">
              <s.icon size={15} className={s.color} />
              <span className="text-xs text-content-subtle">{s.label}</span>
            </div>
            <p className="text-2xl font-bold text-content">{s.value ?? '—'}</p>
            <p className="text-xs text-content-subtle mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-surface rounded-lg border border-border overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <LogIn size={16} className="text-content-subtle" />
          <h3 className="font-semibold text-content text-sm">Historique des connexions</h3>
          <span className="ml-auto text-xs text-content-subtle">{sorted.length} utilisateurs</span>
        </div>

        {isLoading ? (
          <div className="py-12 text-center text-sm text-content-subtle">Chargement...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-raised text-xs text-content-subtle uppercase tracking-wider">
                  <th className="px-5 py-3 text-left">Utilisateur</th>
                  <th className="px-5 py-3 text-left">Rôle</th>
                  <th className="px-5 py-3 text-left">Dernière connexion</th>
                  <th className="px-5 py-3 text-left">Délai</th>
                  <th className="px-5 py-3 text-left">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sorted.map((u) => {
                  const lastLogin = u.lastLogin ?? u.dateDerniereConnexion;
                  return (
                    <tr key={u.idUtilisateur} className="hover:bg-surface-raised transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-primary-light flex items-center justify-center shrink-0">
                            <User size={12} className="text-primary-text" />
                          </div>
                          <div>
                            <p className="font-medium text-content">{u.prenom} {u.nom}</p>
                            <p className="text-xs text-content-subtle">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="px-2 py-0.5 rounded-full bg-surface-overlay text-content-muted text-xs">
                          {u.roleNom ?? u.role}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-content-muted">
                        <div className="flex items-center gap-1.5">
                          <Clock size={12} className="text-content-subtle shrink-0" />
                          {formatDate(lastLogin)}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-xs text-content-subtle">
                        {tempsDepuis(lastLogin)}
                      </td>
                      <td className="px-5 py-3">
                        {u.estVerrouille ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-danger-light text-danger text-xs">
                            <AlertTriangle size={10} /> Bloqué
                          </span>
                        ) : u.isActive ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success-light text-success text-xs">
                            <CheckCircle size={10} /> Actif
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-overlay text-content-subtle text-xs">
                            <XCircle size={10} /> Inactif
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {jamaisConnectes.length > 0 && (
        <div className="bg-warning-light border border-warning rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={15} className="text-warning" />
            <h4 className="font-semibold text-warning text-sm">
              {jamaisConnectes.length} compte(s) jamais utilisé(s)
            </h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {jamaisConnectes.map(u => (
              <span key={u.idUtilisateur} className="px-2 py-1 bg-surface border border-warning rounded text-xs text-content-muted">
                {u.prenom} {u.nom}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ActivityPage;
